import type { ReactNode } from 'react'
import { ExternalLink, Image as ImageIcon, Phone, Reply } from 'lucide-react'
import { cn } from '@/lib/utils'

export type PreviewButton = { type: 'quickReply' | 'url' | 'phoneNumber'; text: string }

export type PreviewProps = {
  header?: string | undefined
  hasMediaHeader?: boolean | undefined
  body: string
  footer?: string | undefined
  buttons: PreviewButton[]
  headerExample?: string | undefined
  bodyExamples?: string[] | undefined
  className?: string | undefined
}

const varRe = /\{\{\s*([^{}]*?)\s*\}\}/g

// Text with each {{n}} shown as its example when there is one, so the owner
// sees what a customer will read. A variable without an example stays visible
// as a chip rather than disappearing.
function Filled({ text, examples }: { text: string; examples: string[] | undefined }) {
  const parts: ReactNode[] = []
  let last = 0
  for (const m of text.matchAll(varRe)) {
    const at = m.index ?? 0
    if (at > last) parts.push(text.slice(last, at))
    const n = Number(m[1])
    const ex = Number.isInteger(n) && n > 0 ? examples?.[n - 1]?.trim() : undefined
    parts.push(
      ex ? (
        <mark key={at} className="rounded bg-primary-soft px-0.5 text-primary-text">{ex}</mark>
      ) : (
        <span key={at} className="rounded border border-dashed border-input px-1 text-xs text-muted-foreground">{m[0]}</span>
      ),
    )
    last = at + m[0].length
  }
  if (last < text.length) parts.push(text.slice(last))
  return <>{parts}</>
}

const buttonIcon = { quickReply: Reply, url: ExternalLink, phoneNumber: Phone } as const

// A WhatsApp-like rendering of a template. It is a picture of the message,
// not the message: WhatsApp's own fonts and spacing differ a little.
export function TemplatePreview({ header, hasMediaHeader, body, footer, buttons, headerExample, bodyExamples, className }: PreviewProps) {
  return (
    <div className={cn('rounded-xl bg-chat p-4', className)}>
      <div className="max-w-[320px] overflow-hidden rounded-[4px_14px_14px_14px] border bg-bubble-in text-[15px] shadow-xs">
        <div className="flex flex-col gap-1 px-3 py-2">
          {hasMediaHeader && (
            <span className="mb-1 flex h-28 items-center justify-center gap-2 rounded-lg bg-muted text-xs text-muted-foreground">
              <ImageIcon className="size-4" aria-hidden="true" />
              Image, video or document header
            </span>
          )}
          {header && (
            <p className="font-semibold [overflow-wrap:anywhere]">
              <Filled text={header} examples={headerExample ? [headerExample] : undefined} />
            </p>
          )}
          <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">
            {body ? <Filled text={body} examples={bodyExamples} /> : <span className="text-muted-foreground">The message text appears here.</span>}
          </p>
          {footer && <p className="text-xs text-muted-foreground">{footer}</p>}
        </div>
        {buttons.length > 0 && (
          <ul className="divide-y border-t">
            {buttons.map((b, i) => {
              const Icon = buttonIcon[b.type]
              return (
                <li key={i} className="flex min-h-10 items-center justify-center gap-1.5 px-3 text-sm font-semibold text-primary-text">
                  <Icon className="size-4" aria-hidden="true" />
                  {b.text || <span className="font-normal text-muted-foreground">Button label</span>}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
